/**
 * Contenido de los coach-marks de /conversation (copy + acento + ícono).
 *
 * Lifteado del bundle de diseño ("Coach-marks Onboarding — Orborix Speak").
 * Marks contextuales; el del micrófono describe el hold del composer
 * compartido (`@orborix/voice-composer`) — los 4 modos de mic murieron con su
 * adopción. Copy ES (voseo) — el UI de speak es español-primero.
 *
 * Los `markId` (`conv_*`) son la clave canónica y DEBEN coincidir con la
 * allowlist del backend (`apps/backend/src/routes/english/coachmarks.ts`).
 */

export type CoachmarkId = 'conv_mic' | 'conv_corr' | 'conv_inspire' | 'conv_text';
export type CoachmarkAccent = 'purple' | 'amber' | 'lime';
export type CoachmarkSide = 'above' | 'below';

export interface CoachmarkContent {
  accent: CoachmarkAccent;
  /** SVG inline (string de confianza, hardcodeado — no input de usuario). */
  icon: string;
  eyebrow: string;
  title: string;
  /** Spotlight circular (botones redondos) vs rectangular (palabra subrayada). */
  round: boolean;
  /** Lado preferido del popover respecto al ancla (el motor reubica si no entra). */
  side: CoachmarkSide;
}

// ─── Íconos (inline SVG, del bundle de diseño) ────────────────────────────────
const ICON_MIC =
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" stroke="none"/><path d="M5 11 a 7 7 0 0 0 14 0"/><line x1="12" y1="18" x2="12" y2="22"/></svg>';
const ICON_PEN =
  '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4 L 20 10 L 9 21 L 3 21 L 3 15 Z"/><path d="M13 5 L 19 11"/></svg>';
const ICON_BULB =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18 H 15"/><path d="M10 21 H 14"/><path d="M12 3 a 6 6 0 0 1 4 10 c -1 1 -1 2 -1 3 H 9 c 0 -1 0 -2 -1 -3 a 6 6 0 0 1 4 -10 Z"/></svg>';
const ICON_KBD =
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2.5" y="6" width="19" height="13" rx="2.5"/><line x1="6" y1="10" x2="6.01" y2="10"/><line x1="10" y1="10" x2="10.01" y2="10"/><line x1="14" y1="10" x2="14.01" y2="10"/><line x1="18" y1="10" x2="18.01" y2="10"/><line x1="9" y1="14" x2="15" y2="14"/></svg>';

const BODY: Record<CoachmarkId, string> = {
  conv_mic:
    'Mantené apretado mientras hablás y soltá cuando termines. Equivocarte está perfecto — para eso estoy.',
  conv_corr: 'Subrayamos lo que podés mejorar. Tocá una palabra marcada para ver cómo se dice bien.',
  conv_inspire: 'Tocá la lamparita y te doy ideas para responder.',
  conv_text: 'Escribí acá lo que querés decir y tocá enviar. No importa si te equivocás — para eso estoy.',
};

export const COACHMARK_CONTENT: Record<CoachmarkId, CoachmarkContent> = {
  conv_mic: {
    accent: 'purple',
    icon: ICON_MIC,
    eyebrow: 'EL MICRÓFONO',
    title: 'Hablá cuando quieras',
    round: true,
    side: 'above',
  },
  conv_corr: {
    accent: 'amber',
    icon: ICON_PEN,
    eyebrow: 'TUS CORRECCIONES',
    title: 'Mirá tus correcciones',
    round: false,
    side: 'below',
  },
  conv_inspire: {
    accent: 'lime',
    icon: ICON_BULB,
    eyebrow: 'INSPIRAR',
    title: '¿No sabés qué decir?',
    round: true,
    side: 'above',
  },
  conv_text: {
    accent: 'purple',
    icon: ICON_KBD,
    eyebrow: 'EL TECLADO',
    title: 'Escribí tu respuesta',
    round: false,
    side: 'above',
  },
};

export const COACHMARK_UI = {
  cta: 'Entendido',
} as const;

/** Resuelve el cuerpo del mark. */
export function coachmarkBody(id: CoachmarkId): string {
  return BODY[id];
}
